<template>
  <section
    v-if="session.status === 'idle' || session.status === 'loading'"
    class="pr-bootstrap-state"
    aria-live="polite"
  >
    <h1>Opening Price Rescue</h1>
    <p>Verifying your platform session and store access.</p>
  </section>
  <section
    v-else-if="session.status === 'offline'"
    class="pr-bootstrap-state pr-bootstrap-state-warning"
    aria-live="assertive"
  >
    <h1>You appear to be offline</h1>
    <p>Reconnect to the internet, then try loading Price Rescue again.</p>
    <button class="pr-btn pr-btn-primary" type="button" @click="retryBootstrap">Try again</button>
  </section>
  <section
    v-else-if="session.status === 'error'"
    class="pr-bootstrap-state pr-bootstrap-state-error"
    aria-live="assertive"
  >
    <h1>Price Rescue could not start</h1>
    <p>{{ session.errorMessage }}</p>
    <p v-if="session.requestId !== undefined" class="pr-tiny pr-muted">
      Request ID: {{ session.requestId }}
    </p>
    <button class="pr-btn pr-btn-primary" type="button" @click="retryBootstrap">Try again</button>
  </section>
  <div v-else>
    <MerchantAppShell>
      <aside v-if="session.offline" class="pr-global-banner pr-global-banner-warning" role="status">
        You are offline. Existing page data may be stale until the connection returns.
      </aside>
      <RouterView />
      <MerchantStoreFeedbackBridge />
      <MerchantToastRegion />
      <MerchantConfirmationDialog />
    </MerchantAppShell>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import {
  MerchantAppShell,
  MerchantConfirmationDialog,
  MerchantStoreFeedbackBridge,
  MerchantToastRegion,
} from "./components/shared/index.js";
import { canEnterRoute, routeDefinitionForNavigation } from "./navigation.js";
import {
  installResponsiveTableLabels,
  type ResponsiveTableLabelsHandle,
} from "./shared/responsive-tables.js";
import { useMerchantSessionStore } from "./stores/session-store.js";

const session = useMerchantSessionStore();
const route = useRoute();
const router = useRouter();
let responsiveTableLabels: ResponsiveTableLabelsHandle | undefined;

onMounted(() => {
  installOnlineStatusListeners();
  responsiveTableLabels = installResponsiveTableLabels();
  void bootstrapSession();
});

watch(
  () => [session.status, session.sessionContext, route.meta["navId"]],
  redirectStateRoutesAfterBootstrap,
);

onUnmounted(() => {
  removeOnlineStatusListeners();
  responsiveTableLabels?.disconnect();
});

function retryBootstrap(): void {
  void bootstrapSession();
}

async function bootstrapSession(): Promise<void> {
  await session.bootstrap();
  redirectStateRoutesAfterBootstrap();
}

function installOnlineStatusListeners(): void {
  window.addEventListener("online", markOnline);
  window.addEventListener("offline", markOffline);
}

function removeOnlineStatusListeners(): void {
  window.removeEventListener("online", markOnline);
  window.removeEventListener("offline", markOffline);
}

function markOnline(): void {
  session.markOnlineStatus(true);
}

function markOffline(): void {
  session.markOnlineStatus(false);
}

function redirectStateRoutesAfterBootstrap(): void {
  if (session.status === "reauthorization-required" && route.name !== "platform-reauthorization") {
    void router.replace({ name: "platform-reauthorization" });
    return;
  }

  if (
    ["access-pending", "suspended", "revoked"].includes(session.status) &&
    route.name !== "access-pending"
  ) {
    void router.replace({ name: "access-pending" });
    return;
  }

  const routeDefinition = routeDefinitionForNavigation(route.meta["navId"]);
  if (
    session.sessionContext !== undefined &&
    routeDefinition !== undefined &&
    !canEnterRoute(routeDefinition, session.sessionContext)
  ) {
    void router.replace({ name: "dashboard" });
  }
}
</script>
